import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
  NavigationMenuContent,
  NavigationMenuLink,
} from "@/components/ui/navigation-menu";
import logo from "@/assets/logo.png?format=webp&w=200";
import logoFallback from "@/assets/logo.png?format=png&w=200";

const textNavLinks = [
  { name: "Home", path: "/" },
  { name: "Packages", path: "/packages" },
  { name: "About", path: "/about" },
  { name: "Contact", path: "/contact" },
];

const serviceDropdownLinks = [
  { name: "All Services", path: "/services", description: "View every service we offer" },
  { name: "Bookkeeping", path: "/bookkeeping", description: "Monthly books and reporting" },
  { name: "VAT", path: "/vat", description: "VAT201 and SARS compliance" },
  { name: "CIPC", path: "/cipc", description: "Annual returns and compliance" },
  { name: "Tools", path: "/tools", description: "Free calculators and guides" },
];

const individualServices = [
  { name: "Monthly Bookkeeping", path: "/monthly-bookkeeping-services" },
  { name: "Payroll & EMP201/501", path: "/payroll-emp201-emp501" },
  { name: "UIF Declarations", path: "/uif-monthly-declarations" },
  { name: "Annual Tax Returns", path: "/annual-tax-returns" },
  { name: "Provisional Tax", path: "/provisional-tax-returns" },
  { name: "CIPC Annual Returns", path: "/cipc-annual-returns" },
  { name: "Beneficial Ownership", path: "/beneficial-ownership-submission" },
  { name: "Workmen's Compensation", path: "/workmens-compensation" },
];

const locationDropdownLinks = [
  { name: "All Locations", path: "/areas", description: "Browse every area we serve" },
  { name: "Cape Town", path: "/areas/cape-town", description: "CBD, Northern Suburbs, Southern Suburbs, Atlantic Seaboard" },
  { name: "Johannesburg", path: "/areas/johannesburg", description: "Sandton, Rosebank, Midrand, Bryanston, Fourways" },
  { name: "Durban", path: "/areas/durban", description: "Umhlanga, Ballito, Durban North, Westville, Hillcrest" },
];

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [locationsOpen, setLocationsOpen] = useState(false);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;
  const isServicesActive =
    location.pathname === "/services" ||
    serviceDropdownLinks.some((l) => l.path === location.pathname) ||
    individualServices.some((l) => l.path === location.pathname);
  const isLocationsActive =
    location.pathname === "/areas" ||
    locationDropdownLinks.some((l) => l.path === location.pathname);

  return (
    <header className="sticky top-0 z-50 w-full bg-background/95 backdrop-blur-md border-b border-border">
      <div className="container mx-auto px-4">
        <nav aria-label="Primary" className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 sm:gap-3" aria-label="Bookkeeping Services South Africa — home">
            <picture>
              <source srcSet={logo} type="image/webp" />
              <img
                src={logoFallback}
                alt=""
                aria-hidden="true"
                className="h-14 sm:h-16 w-auto"
                width={64}
                height={64}
                fetchPriority="high"
                decoding="async"
              />
            </picture>
            <div className="text-left">
              <span className="font-heading text-base sm:text-xl font-bold text-primary leading-tight block">
                Bookkeeping Services
              </span>
              <span className="block text-xs sm:text-sm font-semibold text-primary/70">South Africa</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            <Link
              to="/"
              aria-current={isActive("/") ? "page" : undefined}
              className={`nav-link text-sm px-3 py-2 ${isActive("/") ? "nav-link-active" : ""}`}
            >
              Home
            </Link>

            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger
                    className={`nav-link text-sm ${isServicesActive ? "nav-link-active" : ""}`}
                  >
                    Services
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[500px] p-4">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="flex flex-col gap-1">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 py-1">
                            Service Hubs
                          </p>
                          {serviceDropdownLinks.map((link) => (
                            <NavigationMenuLink asChild key={link.path}>
                              <Link
                                to={link.path}
                                className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                              >
                                <div className="text-sm font-medium leading-none">{link.name}</div>
                                <p className="line-clamp-2 text-xs leading-snug text-muted-foreground mt-1">
                                  {link.description}
                                </p>
                              </Link>
                            </NavigationMenuLink>
                          ))}
                        </div>
                        <div className="flex flex-col gap-1">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 py-1">
                            Individual Services
                          </p>
                          {individualServices.map((link) => (
                            <NavigationMenuLink asChild key={link.path}>
                              <Link
                                to={link.path}
                                className="block rounded-md p-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                              >
                                {link.name}
                              </Link>
                            </NavigationMenuLink>
                          ))}
                        </div>
                      </div>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger
                    className={`nav-link text-sm ${isLocationsActive ? "nav-link-active" : ""}`}
                  >
                    Locations
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[320px] p-4">
                      <div className="flex flex-col gap-1">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 py-1">
                          Areas We Serve
                        </p>
                        {locationDropdownLinks.map((link) => (
                          <NavigationMenuLink asChild key={link.path}>
                            <Link
                              to={link.path}
                              className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                            >
                              <div className="text-sm font-medium leading-none">{link.name}</div>
                              <p className="line-clamp-2 text-xs leading-snug text-muted-foreground mt-1">
                                {link.description}
                              </p>
                            </Link>
                          </NavigationMenuLink>
                        ))}
                      </div>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            {textNavLinks.slice(1).map((link) => (
              <Link
                key={link.path}
                to={link.path}
                aria-current={isActive(link.path) ? "page" : undefined}
                className={`nav-link text-sm px-3 py-2 ${isActive(link.path) ? "nav-link-active" : ""}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <Link to="/get-a-quote">
              <Button className="btn-primary gap-2">
                <Phone className="w-4 h-4" />
                Get a Quote
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            type="button"
            className="lg:hidden p-2 text-foreground"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? "Close menu" : "Open menu"}
            aria-expanded={isOpen}
            aria-controls="mobile-navigation"
          >
            {isOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
          </button>
        </nav>

        {/* Mobile Navigation */}
        {isOpen && (
          <div id="mobile-navigation" className="lg:hidden py-4 border-t border-border animate-fade-in">
            <div className="flex flex-col gap-2">
              <Link
                to="/"
                className={`nav-link py-2 px-3 rounded-md ${isActive("/") ? "nav-link-active" : ""}`}
                onClick={() => setIsOpen(false)}
              >
                Home
              </Link>

              <button
                type="button"
                onClick={() => setServicesOpen(!servicesOpen)}
                className={`nav-link py-2 px-3 rounded-md flex items-center justify-between w-full ${isServicesActive ? "nav-link-active" : ""}`}
                aria-expanded={servicesOpen}
                aria-controls="mobile-services-submenu"
              >
                <span>Services</span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-200 ${servicesOpen ? "rotate-180" : ""}`}
                  aria-hidden="true"
                />
              </button>

              {servicesOpen && (
                <div id="mobile-services-submenu" className="flex flex-col gap-1 pl-4 border-l-2 border-border ml-3">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 py-1">
                    Service Hubs
                  </p>
                  {serviceDropdownLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`nav-link py-2 px-3 rounded-md text-sm ${isActive(link.path) ? "nav-link-active" : ""}`}
                      onClick={() => setIsOpen(false)}
                    >
                      {link.name}
                    </Link>
                  ))}
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 py-1 mt-2">
                    Individual Services
                  </p>
                  {individualServices.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`nav-link py-2 px-3 rounded-md text-sm ${isActive(link.path) ? "nav-link-active" : ""}`}
                      onClick={() => setIsOpen(false)}
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
              )}

              <button
                type="button"
                onClick={() => setLocationsOpen(!locationsOpen)}
                className={`nav-link py-2 px-3 rounded-md flex items-center justify-between w-full ${isLocationsActive ? "nav-link-active" : ""}`}
                aria-expanded={locationsOpen}
                aria-controls="mobile-locations-submenu"
              >
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" aria-hidden="true" />
                  Locations
                </span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-200 ${locationsOpen ? "rotate-180" : ""}`}
                  aria-hidden="true"
                />
              </button>

              {locationsOpen && (
                <div id="mobile-locations-submenu" className="flex flex-col gap-1 pl-4 border-l-2 border-border ml-3">
                  {locationDropdownLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`nav-link py-2 px-3 rounded-md text-sm ${isActive(link.path) ? "nav-link-active" : ""}`}
                      onClick={() => setIsOpen(false)}
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
              )}

              {textNavLinks.slice(1).map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link py-2 px-3 rounded-md ${isActive(link.path) ? "nav-link-active" : ""}`}
                  onClick={() => setIsOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
              <Link to="/get-a-quote" onClick={() => setIsOpen(false)}>
                <Button className="btn-primary w-full gap-2 mt-2">
                  <Phone className="w-4 h-4" />
                  Get a Quote
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
